import { useEffect, useState } from "react";
import type { GraphyClient } from "../api/client";
import { ApiError } from "../api/client";
import type { OverviewResult } from "../api/types";
import { ErrorState, Loading } from "../components/states";
import { Icon, type IconName } from "../components/icons";
import { T } from "../i18n";

const ENTRY_ICON: Record<string, IconName> = {
  findings: "map",
  explore: "explore",
  connectors: "tools",
  ask: "ask",
};

// GY-032 UI: the customer overview landing surface. Renders only authorized,
// backend-computed data; capability sections that are not built yet show their
// honest UNAVAILABLE message, and an empty findings list shows the "not a
// guarantee of safety" note — never a fabricated all-clear.

interface Props {
  client: GraphyClient;
  projectId: string;
  onNavigate: (tab: "map" | "explore") => void;
  pendingCount: number; // live updates since the view loaded (GY-020)
  onRefreshed: () => void;
}

export function Overview({ client, projectId, onNavigate, pendingCount, onRefreshed }: Props) {
  const [ov, setOv] = useState<OverviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    client
      .overview({ projectId })
      .then((r) => live && setOv(r))
      .catch((e) => {
        if (e instanceof ApiError || e instanceof Error) setError(true);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [client, projectId, reloadToken]);

  if (error) return <ErrorState />;
  if (loading || !ov) return <Loading />;

  return (
    <div className="overview">
      <div className="page-head">
        <h2>{T.app.overview}</h2>
        {ov.coverage.status === "ok" && (
          <p>{T.overviewView.coverageDetail(ov.coverage.repos.join(", "), ov.coverage.symbolCount)}</p>
        )}
      </div>
      {pendingCount > 0 && (
        <div className="banner banner-partial app-stale" role="status">
          <span>{T.overviewView.newFindings(pendingCount)}</span>
          <button
            className="btn btn-ghost"
            onClick={() => {
              setReloadToken((t) => t + 1);
              onRefreshed();
            }}
          >
            {T.overviewView.refresh}
          </button>
        </div>
      )}
      <div className="overview-grid">
        <section className="card">
          <h3>{T.overviewView.findings}</h3>
          <div className="stat-big">{ov.findings.total}</div>
          {ov.findings.safetyNote ? (
            <p className="muted">{ov.findings.safetyNote}</p>
          ) : (
            <>
              <div className="stat-row">
                <span className="muted">{T.overviewView.bySeverity}</span>
                {Object.entries(ov.findings.bySeverity)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => (
                    <span key={k} className={`tag tag-${k}`}>
                      {k}: {v}
                    </span>
                  ))}
              </div>
              <div className="stat-row">
                <span className="muted">{T.overviewView.byStatus}</span>
                {Object.entries(ov.findings.byStatus)
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => (
                    <span key={k} className="tag">
                      {k}: {v}
                    </span>
                  ))}
              </div>
            </>
          )}
        </section>

        <section className="card">
          <h3>{T.overviewView.coverage}</h3>
          {ov.coverage.status === "ok" ? (
            <p>{T.overviewView.coverageDetail(ov.coverage.repos.join(", "), ov.coverage.symbolCount)}</p>
          ) : (
            <p className="muted">{ov.coverage.message}</p>
          )}
        </section>

        <section className="card">
          <h3>{T.overviewView.communities}</h3>
          {ov.communities.status === "ok" ? (
            <p>{T.overviewView.communitiesCount(ov.communities.count, ov.communities.largestSize)}</p>
          ) : (
            <p className="muted">{ov.communities.message}</p>
          )}
        </section>

        {(["reviews", "verification", "knowledge"] as const).map((key) => (
          <section className="card" key={key}>
            <h3>{T.overviewView[key]}</h3>
            <p className="muted">{ov[key].message}</p>
          </section>
        ))}
      </div>

      <section className="card">
        <h3>{T.overviewView.recent}</h3>
        {ov.findings.recent.length === 0 ? (
          <p className="muted">{T.overviewView.noFindings}</p>
        ) : (
          <ul className="node-list">
            {ov.findings.recent.map((f) => (
              <li key={f.finding_id}>
                <span>{f.title}</span>
                <span>
                  <span className={`tag tag-${f.severity}`}>{f.severity}</span>
                  <span className="tag">{f.status}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h3>{T.overviewView.entryPoints}</h3>
        <div className="entry-points">
          {ov.entryPoints.map((e) => {
            const C = Icon[ENTRY_ICON[e.key] ?? "chevron"];
            return (
              <button
                key={e.key}
                className="btn-icon"
                disabled={!e.enabled}
                title={e.reason ?? undefined}
                onClick={() => {
                  if (e.key === "explore") onNavigate("explore");
                  else if (e.key === "findings") onNavigate("map");
                }}
              >
                <C size={15} />
                {e.label}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
